import React from 'react';
import {t} from 'i18next';
import {PageIndicator} from '../../PageIndicator/PageIndicator';
import {ItemsHeaderWrapper, LayoutButton, LayoutsWrapper} from './ItemsHeader.style';
import {LayoutViewType} from '../../../models/LayoutViewType';
import {generateIdentifier} from '../../../utils/IdentityUtils';
import {PageSize} from '../../PageSizePicker/PageSize';
import {TemplatePropertySettings} from '../../../models/TemplatePropertySettings';
import {getDefaultValueOfComponent, isVisibleComponent} from '../../../utils/TemplateUtils';
import {
    ITEMS_HEADER_LAYOUT_GRID,
    ITEMS_HEADER_LAYOUT_LIST,
    ITEMS_HEADER_LAYOUT_TABLE,
    ITEMS_HEADER_LAYOUTS,
    ITEMS_HEADER_MOBILE_LAYOUT_GRID,
    ITEMS_HEADER_MOBILE_LAYOUT_LIST,
    ITEMS_HEADER_MOBILE_LAYOUT_TABLE,
    ITEMS_HEADER_MOBILE_LAYOUTS,
    ITEMS_HEADER_MOBILE_PAGE_INDICATOR,
    ITEMS_HEADER_MOBILE_PAGE_SIZE,
    ITEMS_HEADER_MOBILE_RESULT_INFO,
    ITEMS_HEADER_PAGE_INDICATOR,
    ITEMS_HEADER_PAGE_SIZE,
    ITEMS_HEADER_RESULT_INFO
} from '../../../init/ui_component_constants';
import {convertToStringArray} from '../../../utils';
import {defaultItemsHeaderLayouts, defaultItemsHeaderMobileLayouts} from '../../../init/ui_component_defaults';
import {DropdownOptionProps} from '../../../models/DropdownProps';

export interface ItemsHeaderProps {
    readonly templatePropertySettings: Map<string, TemplatePropertySettings>;
    readonly isMobileView: boolean;
    readonly isMobileResolution: boolean;
    readonly page: number;
    readonly pageCount: number;
    readonly itemCount: number;
    readonly itemCountTotal: number;
    readonly layoutView: string;
    readonly pageSize: number;
    readonly pageSizeDataSource: string[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onLayoutViewChange: (layoutView: LayoutViewType) => void;
}

const getLayoutGridView = (props: ItemsHeaderProps) => {
    if (!(isVisibleComponent(ITEMS_HEADER_LAYOUT_GRID, props.templatePropertySettings))
        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_HEADER_MOBILE_LAYOUT_GRID, props.templatePropertySettings)))) {
        return;
    }

    return (
        <LayoutButton id={generateIdentifier(LayoutViewType.GRID)}
                      className={`${LayoutViewType.GRID}-view`}
                      icon='grid-view'
                      mode='integrated'
                      selected={props.layoutView === LayoutViewType.GRID}
                      aria-label={t('DATA_HEADER_GRID_VIEW_LABEL')}
                      title={t('DATA_HEADER_GRID_VIEW_LABEL')}
                      onClick={() => props.onLayoutViewChange(LayoutViewType.GRID)}/>
    );
};

const getLayoutListView = (props: ItemsHeaderProps) => {
    if (!(isVisibleComponent(ITEMS_HEADER_LAYOUT_LIST, props.templatePropertySettings))
        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_HEADER_MOBILE_LAYOUT_LIST, props.templatePropertySettings)))) {
        return;
    }

    return (
        <LayoutButton id={generateIdentifier(LayoutViewType.LIST)}
                      className={`${LayoutViewType.LIST}-view`}
                      icon='list-view'
                      mode='integrated'
                      selected={props.layoutView === LayoutViewType.LIST}
                      title={t('DATA_HEADER_LIST_VIEW_LABEL')}
                      onClick={() => props.onLayoutViewChange(LayoutViewType.LIST)}/>
    );
};

const getLayoutTableView = (props: ItemsHeaderProps) => {
    if (!(isVisibleComponent(ITEMS_HEADER_LAYOUT_TABLE, props.templatePropertySettings))
        && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_HEADER_MOBILE_LAYOUT_TABLE, props.templatePropertySettings)))) {
        return;
    }

    return (
        <LayoutButton id={generateIdentifier(LayoutViewType.TABLE)}
                      className={`${LayoutViewType.TABLE}-view`}
                      icon='table'
                      mode='integrated'
                      selected={props.layoutView === LayoutViewType.TABLE}
                      title={t('DATA_HEADER_TABLE_VIEW_LABEL')}
                      onClick={() => props.onLayoutViewChange(LayoutViewType.TABLE)}/>
    );
};

/**
 * @name ItemsHeader
 * @description
 * Component for the footer of the item visualisation
 */
export const ItemsHeader: React.VFC<ItemsHeaderProps> = (props) => {
    let layouts: string[] = [];
    if (props.isMobileView) {
        layouts = convertToStringArray([getDefaultValueOfComponent(ITEMS_HEADER_MOBILE_LAYOUTS, props.templatePropertySettings)]);
        if (layouts.length === 0) {
            layouts = defaultItemsHeaderMobileLayouts;
        }
    } else {
        layouts = convertToStringArray([getDefaultValueOfComponent(ITEMS_HEADER_LAYOUTS, props.templatePropertySettings)]);
        if (layouts.length === 0) {
            layouts = defaultItemsHeaderLayouts;
        }
    }

    const getPageSizeOptions = (): DropdownOptionProps[] => {
        let options: DropdownOptionProps[] = [];
        let pageSizes: number[] = [props.pageSize];
        props.pageSizeDataSource.map(pageSize => {
            let pageSizeItem = Number(pageSize);
            if ((pageSizeItem !== undefined) && pageSizeItem && (!isNaN(pageSizeItem)) && (pageSizeItem > 1) && (!pageSizes.includes(pageSizeItem))) {
                pageSizes.push(pageSizeItem);
            }
        });

        pageSizes = pageSizes.sort((value1, value2) => value1 - value2);
        pageSizes.map(pageSize => {
            let pageSizeOption: DropdownOptionProps = {
                name: pageSize.toString(),
                value: pageSize
            } as DropdownOptionProps;
            options.push(pageSizeOption);
        })

        return options;
    };

    return (
        <ItemsHeaderWrapper id={generateIdentifier()}>
            {(isVisibleComponent(ITEMS_HEADER_RESULT_INFO, props.templatePropertySettings)
                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_HEADER_MOBILE_RESULT_INFO, props.templatePropertySettings)))) &&
                <div id={generateIdentifier()}>
                    {t('DOWNLOAD_CENTER_RESULT_INFO_TEXT', {
                        pageItemsStartNumber: ((props.page - 1) * props.itemCount) + Math.min(1, props.itemCount),
                        pageItemsCount: ((props.page - 1) * props.itemCount) + props.itemCount,
                        totalItemsCount: props.itemCountTotal
                    })}
                </div>
            }
            {(isVisibleComponent(ITEMS_HEADER_PAGE_INDICATOR, props.templatePropertySettings)
                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_HEADER_MOBILE_PAGE_INDICATOR, props.templatePropertySettings)))) &&
                <PageIndicator templatePropertySettings={props.templatePropertySettings}
                               isMobileResolution={props.isMobileResolution}
                               pages={props.pageCount}
                               selected={props.page}
                               onPageSelect={props.onPageChange}/>
            }
            <LayoutsWrapper>
                {(isVisibleComponent(ITEMS_HEADER_PAGE_SIZE, props.templatePropertySettings))
                    && ((!props.isMobileView) || (props.isMobileView && isVisibleComponent(ITEMS_HEADER_MOBILE_PAGE_SIZE, props.templatePropertySettings))) &&
                    <PageSize dataSource={getPageSizeOptions()}
                              defaultValue={props.pageSize}
                              resetState={false}
                              resetInput={false}
                              disabled={false}
                              onChange={props.onPageSizeChange}/>
                }
                {layouts?.map(layout => {
                    switch (layout) {
                        case ITEMS_HEADER_LAYOUT_LIST:
                            return getLayoutListView(props);
                        case ITEMS_HEADER_LAYOUT_TABLE:
                            return getLayoutTableView(props);
                        case ITEMS_HEADER_LAYOUT_GRID:
                        default:
                            return getLayoutGridView(props);
                    }
                })}
            </LayoutsWrapper>
        </ItemsHeaderWrapper>
    );
};
